import React, { useState } from 'react';

interface SmartPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [inputValue, setInputValue] = useState('');

  // Helper to render a page button
  const renderPageButton = (page: number) => (
    <button
      key={page}
      className={`mx-1 px-3 py-1 rounded ${
        page === currentPage
          ? 'bg-green-500 text-white font-bold'
          : 'bg-white text-gray-600 hover:bg-green-100'
      }`}
      onClick={() => onPageChange(page)}
      disabled={page === currentPage}
    >
      {page}
    </button>
  );

  // Calculate which page numbers to show (standard web convention)
  let pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    // Show all pages if not too many
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    // Always show first page
    pages.push(1);
    // Show left ellipsis if needed
    if (currentPage > 4) pages.push('ellipsis');
    // Show up to 2 pages before and after current
    for (let i = Math.max(2, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }
    // Show right ellipsis if needed
    if (currentPage < totalPages - 3) pages.push('ellipsis');
    // Always show last page
    pages.push(totalPages);
  }

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value.replace(/[^0-9]/g, ''));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const page = Number(inputValue);
      if (page >= 1 && page <= totalPages) {
        onPageChange(page);
      }
      setInputValue('');
    }
  };

  return (
    <div className="flex items-center justify-center space-x-1 mt-4">
      {/* Prev */}
      <button
        className="px-2 py-1 text-gray-500 hover:text-green-600"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        &lt;
      </button>

      {/* Page Buttons */}
      {pages.map((p, idx) => {
        if (p === 'ellipsis') {
          return (
            <span key={`ellipsis-${idx}`} className="mx-1 text-gray-400">
              ...
            </span>
          );
        }
        return renderPageButton(p as number);
      })}

      {/* Always show input box at the end */}
      <input
        key="input"
        type="number"
        min={1}
        max={totalPages}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        placeholder="Go"
        className="w-14 px-2 py-1 border rounded text-center mx-1 focus:outline-none focus:ring-2 focus:ring-green-400"
      />

      {/* Next */}
      <button
        className="px-2 py-1 text-gray-500 hover:text-green-600"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    </div>
  );
};

export default SmartPagination; 